import api, { ApiResponse } from './api';
import type {
  AiSuggestResult,
  ReportComposerPreviewContext,
  ReportTemplate,
} from '@safetyhub/shared';

/**
 * 렌더/작성용 템플릿 조회 (Phase 4a-2).
 * GET /report-composer/templates/:key (인증, enabled 전용) → ReportTemplate.
 */
export async function getRenderTemplate(key: string): Promise<ReportTemplate> {
  const response = await api.get<ApiResponse<ReportTemplate>>(
    `/report-composer/templates/${encodeURIComponent(key)}`,
  );
  return response.data.data!;
}

/**
 * enabled 템플릿 목록 조회 (Phase B — 진입점 카드용).
 * GET /report-composer/templates (인증, enabled 전용) → ReportTemplate[].
 * companyId 전달 시 서버가 노출 정책(policy.audience 기관 축)을 필터해 내려준다
 * (2026-07-26 — 멤버십 검증 포함, 미전달=무필터 하위호환). 업종 축은 현장 속성이라
 * 목록 단계 미적용 — 생성 시 서버 가드가 최종 차단한다.
 */
export async function listEnabledTemplates(
  companyId?: string,
): Promise<ReportTemplate[]> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const response = await api.get<ApiResponse<ReportTemplate[]>>(
    `/report-composer/templates${query}`,
  );
  return response.data.data ?? [];
}

/**
 * Composer 보고서의 live(현재값) content 조회 (Phase D).
 * GET /reports/:id/composer-live (인증) → { content } — 보고서 현재 컨텍스트로
 * 데이터 바인딩을 재resolve한 현재값(select경로 키, snapshot과 동일 형식).
 * live lifecycle 바인딩 렌더 시 snapshot 대신 이 값을 읽는다.
 */
export async function getComposerLiveData(
  reportId: string,
  companyId?: string,
): Promise<Record<string, unknown>> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const response = await api.get<ApiResponse<{ content: Record<string, unknown> }>>(
    `/reports/${encodeURIComponent(reportId)}/composer-live${query}`,
  );
  return response.data.data?.content ?? {};
}

/**
 * Composer 작성폼 초기값(prefill) 조회 (N1 — 편집 가능한 prefill).
 * POST /report-composer/templates/:key/prefill (인증) → { [bindingKey]: value }.
 * input 바인딩의 prefillFrom(data/시스템 출처)을 컨텍스트로 1차 해석한 초기값.
 * 작성자가 수정 가능하며, 최종 편집값이 서버 compose(input override)로 박제된다.
 */
export async function getComposerPrefill(
  key: string,
  context: ReportComposerPreviewContext,
): Promise<Record<string, unknown>> {
  const response = await api.post<ApiResponse<Record<string, unknown>>>(
    `/report-composer/templates/${encodeURIComponent(key)}/prefill`,
    { context },
  );
  return response.data.data ?? {};
}

/**
 * 카탈로그 행 즉시 등록 (submitAction 모달 — 결정 2026-07-17 "등록 모달 = 즉시 등록").
 * POST /report-composer/catalog/:catalogKey/rows (인증) → 등록/갱신된 행(카탈로그 rows 형식, id 포함).
 * keyed upsert writer 화이트리스트(upsertKey 선언 카탈로그)만 허용 — 그 외/키 값 누락은 400,
 * 타 조직 site는 403. 반환 행은 콤보 세팅(합성 id)과 제안 rows 즉시 반영에 사용한다.
 */
export async function submitComposerCatalogRow(
  catalogKey: string,
  siteId: string,
  values: Record<string, string>,
): Promise<Record<string, unknown>> {
  const response = await api.post<ApiResponse<Record<string, unknown>>>(
    `/report-composer/catalog/${encodeURIComponent(catalogKey)}/rows`,
    { siteId, values },
  );
  return response.data.data ?? {};
}

/**
 * AI 제안(aiSelect) 후보 조회 (P3 — 작성 폼 AI 제안 위젯).
 * POST /report-composer/ai-suggest (인증) → AiSuggestResult(후보 목록).
 * actionId가 실행할 제안 액션(SMS 통계 등)을 식별하고, context(현장/점검/조직)로
 * 후보를 산출한다. companyId 멤버십 검증(403). 액션에 따라 점검 컨텍스트가 없으면 400.
 */
export async function postAiSuggest(
  actionId: string,
  context: ReportComposerPreviewContext,
  params?: Record<string, unknown>,
): Promise<AiSuggestResult> {
  const response = await api.post<ApiResponse<AiSuggestResult>>(
    '/report-composer/ai-suggest',
    { actionId, context, params },
  );
  return response.data.data ?? { candidates: [] };
}
